import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ChevronRight, Users, User, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Employee {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  position?: string;
  department?: string;
  manager_id?: string | null;
  invitation_status: string;
  accepted_at: string | null;
  invited_at: string;
}

interface EmployeeHierarchyDialogProps {
  employee: Employee | null;
  employees: Employee[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectEmployee: (employee: Employee) => void;
}

export const EmployeeHierarchyDialog = ({
  employee,
  employees,
  open,
  onOpenChange,
  onSelectEmployee,
}: EmployeeHierarchyDialogProps) => {
  if (!employee) return null;

  const displayName = employee.first_name && employee.last_name
    ? `${employee.first_name} ${employee.last_name}`
    : employee.email || "Sans nom";

  const initials = employee.first_name && employee.last_name
    ? `${employee.first_name[0]}${employee.last_name[0]}`
    : "?";

  const isActive = employee.accepted_at !== null;

  // Get manager chain (upward hierarchy)
  const getManagerChain = (emp: Employee): Employee[] => {
    const chain: Employee[] = [];
    let current = emp;
    while (current.manager_id) {
      const manager = employees.find(e => e.id === current.manager_id);
      if (manager) {
        chain.unshift(manager);
        current = manager;
      } else {
        break;
      }
    }
    return chain;
  };

  // Get direct reports
  const getDirectReports = (empId: string): Employee[] => {
    return employees.filter(e => e.manager_id === empId);
  };

  const managerChain = getManagerChain(employee);
  const directReports = getDirectReports(employee.id);

  const EmployeeCard = ({ emp, onClick, isHighlighted = false }: { emp: Employee; onClick?: () => void; isHighlighted?: boolean }) => {
    const name = emp.first_name && emp.last_name
      ? `${emp.first_name} ${emp.last_name}`
      : emp.email || "Sans nom";
    const ini = emp.first_name && emp.last_name
      ? `${emp.first_name[0]}${emp.last_name[0]}`
      : "?";
    const active = emp.accepted_at !== null;
    const reports = employees.filter(e => e.manager_id === emp.id).length;

    return (
      <button
        onClick={onClick}
        disabled={!onClick}
        className={cn(
          "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
          isHighlighted
            ? "bg-primary/10 border-primary/30"
            : "bg-card hover:bg-muted/50 border-border",
          onClick && "cursor-pointer hover:border-primary/40"
        )}
      >
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback className={cn(
            "text-sm font-medium",
            isHighlighted ? "bg-primary text-primary-foreground" : "bg-muted"
          )}>
            {ini}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{name}</span>
            {reports > 0 && (
              <Badge variant="secondary" className="text-xs shrink-0">
                <Users className="h-3 w-3 mr-1" />
                {reports}
              </Badge>
            )}
          </div>
          {(emp.position || emp.department) && (
            <p className="text-sm text-muted-foreground truncate">
              {emp.position}{emp.position && emp.department && " • "}{emp.department}
            </p>
          )}
        </div>
        <Badge
          variant={active ? "default" : "outline"}
          className={cn(
            "shrink-0 text-xs",
            active && "bg-success text-success-foreground"
          )}
        >
          {active ? "Actif" : "En attente"}
        </Badge>
        {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-4 sm:p-6 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Hiérarchie
          </DialogTitle>
          <DialogDescription>
            Position de {displayName} dans l'organigramme
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 px-4 sm:px-6 pb-4 sm:pb-6">
          <div className="space-y-4">
            {/* Manager chain */}
            {managerChain.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <ArrowUp className="h-4 w-4" />
                  Supérieurs hiérarchiques
                </div>
                <div className="space-y-2 pl-2 border-l-2 border-muted ml-2">
                  {managerChain.map((manager, index) => (
                    <div key={manager.id} className="relative">
                      <div className="absolute -left-[11px] top-1/2 w-2 h-px bg-muted" />
                      <EmployeeCard
                        emp={manager}
                        onClick={() => onSelectEmployee(manager)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current employee */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <User className="h-4 w-4" />
                Employé sélectionné
              </div>
              <EmployeeCard emp={employee} isHighlighted />
            </div>

            {/* Direct reports */}
            {directReports.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Users className="h-4 w-4" />
                  Équipe ({directReports.length} membre{directReports.length > 1 ? "s" : ""})
                </div>
                <div className="space-y-2 pl-2 border-l-2 border-primary/30 ml-2">
                  {directReports.map((report) => (
                    <div key={report.id} className="relative">
                      <div className="absolute -left-[11px] top-1/2 w-2 h-px bg-primary/30" />
                      <EmployeeCard
                        emp={report}
                        onClick={() => onSelectEmployee(report)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No hierarchy message */}
            {managerChain.length === 0 && directReports.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                <User className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune relation hiérarchique définie</p>
                <p className="text-xs mt-1">Assignez un manager dans la liste des employés</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
